import { type DateValue, getLocalTimeZone } from "@internationalized/date";

/**
 * Determines if there is sufficient historical data for a valid prior period comparison.
 *
 * @param priorPeriod - The prior period data including date range
 * @param dateRange - The currently selected date range
 * @param limits - The total available data range (minDate to maxDate)
 * @returns true if there's enough historical data for comparison, false otherwise
 *
 * Logic: If the selected date range is more than half of the total available data period,
 * there isn't enough historical data for a fair prior period comparison.
 */
export function hasValidPriorPeriodComparison(
  priorPeriod: { from: string; to: string; value: number } | undefined,
  dateRange: { start: DateValue; end: DateValue } | null | undefined,
  limits: { minDate: DateValue; maxDate: DateValue } | undefined,
): boolean {
  if (!priorPeriod) return false;
  if (!dateRange || !limits) return true; // If no dateRange or limits, trust backend

  // Calculate the total available data period length
  const totalDataLength =
    limits.maxDate.toDate(getLocalTimeZone()).getTime() - limits.minDate.toDate(getLocalTimeZone()).getTime();

  // Calculate the selected date range length
  const selectedLength =
    dateRange.end.toDate(getLocalTimeZone()).getTime() - dateRange.start.toDate(getLocalTimeZone()).getTime();

  // If selected range is more than half of available data, not enough prior period data
  return selectedLength <= totalDataLength / 2;
}
